import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { MainActions } from 'screens/main';
import { useReduxSelector, useReduxDispatch, usePushLog } from 'services/hooks';
import { checkNtuHighestLevel, objectEqual, recordValue } from 'services/utils';
import { PlayerActions } from 'services/redux';
import { callFunctionAction } from 'services/api';

import {
  IEducationState,
  EducationPage,
  EducationModal,
} from '../../../education.typing';

export interface IStartTestProps {
  level: string;
  onCloseModal: () => void;
  updateEducationState: (partialState: Partial<IEducationState>) => void;
  sector?: string;
}

export const useStartTest = (props: IStartTestProps) => {
  const { level, sector, updateEducationState } = props;

  const [isLoading, setIsLoading] = useState(false);

  const version = useReduxSelector((redux) => redux.app.version);
  const educationLevels = useReduxSelector(
    (redux) => redux.app.educationLevels,
    objectEqual
  );

  const isGamifiedEducation = useReduxSelector((redux) =>
    Boolean(redux.app.gameMods?.gamifiedEducation)
  );
  const isGamifiedEducationPassed = useReduxSelector((redux) =>
    Boolean(redux.player.gamifiedEducation?.isPassed)
  );

  const isPhdHard = useMemo(
    () => checkNtuHighestLevel({ educationLevels, level, version }),
    [educationLevels, level, version]
  );

  const { callAction } = useReduxDispatch();

  const dispatch = useDispatch();
  const pushLog = usePushLog();

  const setShowBottomBar = callAction(MainActions.setShowBottomBar);

  const isGamifiedLevel =
    isGamifiedEducation && level === "Bachelor's" && !isGamifiedEducationPassed;

  const cost = isGamifiedLevel
    ? { energy: 10, money: 500 }
    : recordValue(educationLevels, level)?.price;

  if (!cost) {
    return null;
  }

  const { energy, money } = cost;

  const onStartTest = async () => {
    // If loading, ignore
    if (isLoading) {
      return;
    }

    // Set loading
    setIsLoading(true);

    try {
      // Update player state on backend & get questions
      const result = await callFunctionAction('@EDUCATION/START', {
        level,
        sector,
      });

      // If degree is exceeded, show the modal
      if (result.isExceeded) {
        updateEducationState({ modal: EducationModal.ExceededDegree });
        return;
      }

      // If gamified education is enabled and the level is Bachelor's,
      // player should be moved to the education game by GamifiedEducation screen after player's state is updated.
      // (player.gamifiedEducation.sector is set to the sector and EducationGame component is rendered)
      // so there is no need to update the state and check for questions.
      if (isGamifiedLevel) {
        dispatch(PlayerActions.merge(result.mergePlayer));

        pushLog({
          type: 'education',
          action: 'start',
          params: {
            level,
            sector,
            energy: -energy,
            money: money && -money,
          },
        });

        return;
      }

      // If no questions, throw an error
      if (!result.questions?.length) {
        throw new Error('No questions');
      }

      // Update state
      updateEducationState({
        // Set questions from the backend response
        questions: result.questions,
        // Open Test page
        page: EducationPage.Test,
        // Close modal
        modal: undefined,
      });

      // Hide bottom bar
      setShowBottomBar(false);

      // Synchronize player state in redux
      dispatch(PlayerActions.merge(result.mergePlayer));
    } catch (error) {
      updateEducationState({
        page: EducationPage.Level,
        modal: undefined,
      });

      console.error(error);
    } finally {
      setIsLoading(false);
    }

    pushLog({
      type: 'education',
      action: 'start',
      params: {
        level,
        sector,
        energy: -energy,
        money: money && -money,
      },
    });
  };

  return { isLoading, energy, money, isPhdHard, isGamifiedLevel, onStartTest };
};
